import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Star, Search, ChevronDown, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { formatPrice } from '../utils/priceFormatter';
import { useDropdownContext } from '../contexts/DropdownContext';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { MarketData } from '../services/binanceService';
import { useTheme } from '../contexts/ThemeContext';
import Loader from './Loader';

interface MarketTickerProps {
  selectedMarket: string;
  onMarketChange: (market: string) => void;
  onSymbolSelect?: (symbol: string) => void;
  selectedSymbol?: string; // Add selectedSymbol prop for mobile single asset display
}

const MarketTicker: React.FC<MarketTickerProps> = ({
  selectedMarket,
  onMarketChange,
  onSymbolSelect,
  selectedSymbol: propSelectedSymbol,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'favorites' | 'all'>('favorites');
  //  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTCUSDT');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('ETHUSDT');
  const { isOpen, toggleDropdown } = useDropdownContext();
  const { theme } = useTheme();
  
  // Use prop selectedSymbol if provided, otherwise use internal state
  const currentSelectedSymbol = propSelectedSymbol || selectedSymbol;
  
  // Detect mobile screen size
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Use Binance market data hook
  const {
    isLoading,
    error,
    isConnected,
    refreshData,
    reconnect,
    getMarketDataByCategory,
    getTopGainers,
    getTopLosers,
    getHighestVolume,
  } = useBinanceMarketData();

  const [favorites, setFavorites] = useState<Set<string>>(() => {
    // Initialize favorites from localStorage or use default values
    const savedFavorites = localStorage.getItem('marketTickerFavorites');
    if (savedFavorites) {
      try {
        return new Set(JSON.parse(savedFavorites));
      } catch (error) {
        // Handle error silently
      }
    }
    // Default favorites with USDT format
    //   return new Set(['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'ADAUSDT', 'DOTUSDT', 'LINKUSDT', 
    // 'EURUSD', 'GBPUSD', 'USDJPY']);
    return new Set(['ETHUSDT', 'BTCUSDT', 'SOLUSDT', 'ADAUSDT', 'DOTUSDT', 'LINKUSDT', 'EURUSD', 'GBPUSD', 'USDJPY']);
  });

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('marketTickerFavorites', JSON.stringify(Array.from(favorites)));
  }, [favorites]);

  // Scroll state for showing scroll indicator
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  
  // Handle scroll to show/hide indicator
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px threshold
    
    setShowScrollIndicator(isScrollable && !isAtBottom);
  };

  // Get market data based on selected market
  const getMarketDataForSelectedMarket = (): MarketData[] => {
    switch (selectedMarket) {
      case 'Top Movers':
        return [...getTopGainers(15), ...getTopLosers(15)];
      case 'Popular':
        // Return top volume assets
        return getHighestVolume(20);
      case 'Crypto':
        return getMarketDataByCategory('crypto');
      case 'Forex':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include forex pairs
        return [];
      case 'Commodities':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include commodities
        return [];
      case 'Indices':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include indices
        return [];
      case 'Stocks':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include stocks
        return [];
      case 'Futures':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include futures
        return [];
      default:
        return getMarketDataByCategory('crypto');
    }
  };

  const marketData = getMarketDataForSelectedMarket();

  // Apply search filter
  const filteredData = marketData.filter(item => {
    const matchesSearch =
      item.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === 'favorites') {
      return matchesSearch && favorites.has(item.symbol);
    }
    return matchesSearch;
  });

  // For mobile: find the current selected symbol data
  const currentSymbolData = marketData.find(item => item.symbol === currentSelectedSymbol);

  const toggleFavorite = (symbol: string) => {
    setFavorites(prevFavorites => {
      const newFavorites = new Set(prevFavorites);
      if (newFavorites.has(symbol)) {
        newFavorites.delete(symbol);
      } else {
        newFavorites.add(symbol);
      }
      return newFavorites;
    });
  };

  // Handle category selection - all categories work the same way now
  const handleCategoryClick = (category: string) => {
    onMarketChange(category);
    toggleDropdown('marketDropdown');
  };



  // Mobile single asset display with scrollable list
  if (isMobile) {
    return (
      <div className={`card p-2 relative ${theme === 'light' ? 'bg-white border border-0  border-gray-100' : ''}`}>
        {/* Search for mobile */}
        <div className="relative mb-3">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
            theme === 'light' ? 'text-gray-400' : 'theme-text-quaternary'
          }`} />
          <input
            type="text"
            placeholder="Search markets..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#2AB7A6] focus:border-transparent transition-colors duration-200 ${
              theme === 'light' 
                ? 'bg-gray-50 border-gray-300 text-gray-700 placeholder-gray-400 focus:bg-white' 
                : 'theme-bg-quaternary theme-border theme-text-primary placeholder-theme-text-quaternary'
            }`}
          />
        </div>

        {/* Mobile scrollable single item list */}
        <div className="space-y-2 overflow-y-auto scrollbar-hide" 
             style={{ 
               scrollbarWidth: 'none', 
               msOverflowStyle: 'none',
               maxHeight: '200px'
             }}>
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8 min-h-[100px]">
              <Loader 
                scale='0.5x'
                size="sm" 
                title="" 
                subtitle="" 
              />
            </div>
          )}
          
          {/* Error State */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="text-red-400 text-center">
                <p className="text-xs font-medium">Failed to load market data</p>
                <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>{error}</p>
              </div>
              <button
                onClick={refreshData}
                className="px-4 py-2 bg-[#2AB7A6] text-black text-xs font-medium rounded-lg hover:bg-[#2AB7A6]/90 transition-colors"
              >
                Retry
              </button>
            </div>
          )}
          
          {/* Market Data - Single item view */}
          {!isLoading && !error && filteredData.map(item => (
            <div
              key={item.symbol}
              onClick={() => {
                setSelectedSymbol(item.symbol);
                onSymbolSelect?.(item.symbol);
              }}
              className={`flex items-center justify-between p-3 rounded-lg transition-colors duration-200 cursor-pointer ${
                theme === 'light' 
                  ? currentSelectedSymbol === item.symbol
                    ? 'bg-gray-100 border border-gray-200'
                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                  : currentSelectedSymbol === item.symbol
                    ? 'theme-bg-quaternary'
                    : 'theme-bg-quaternary hover:theme-bg-quinary'
              }`}
            >
              <div className="flex items-center space-x-3">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    toggleFavorite(item.symbol);
                  }}
                  className={`text-xs transition-all duration-200 hover:scale-110 ${
                    favorites.has(item.symbol)
                      ? 'text-yellow-400'
                      : theme === 'light' 
                        ? 'text-gray-400 hover:text-yellow-400' 
                        : 'theme-text-quaternary hover:text-yellow-400'
                  }`}
                >
                  <Star
                    className={`h-4 w-4 ${favorites.has(item.symbol) ? 'fill-current' : ''}`}
                  />
                </button>
                <div>
                  <div className={`text-sm font-medium ${
                    currentSelectedSymbol === item.symbol 
                      ? (theme === 'light' ? 'text-gray-900' : 'theme-text-primary')
                      : 'theme-text-secondary'
                  }`}>
                    {item.symbol}
                  </div>
                  <div className={`text-xs ${
                    currentSelectedSymbol === item.symbol 
                      ? (theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary')
                      : 'theme-text-quaternary'
                  }`}>{item.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-medium ${
                  currentSelectedSymbol === item.symbol 
                    ? (theme === 'light' ? 'text-gray-900' : 'theme-text-primary')
                    : 'theme-text-secondary'
                }`}>
                  <span dangerouslySetInnerHTML={{ __html: formatPrice(parseFloat(item.price), item.symbol) }} />
                </div>
                <div
                  className={`text-xs font-medium flex items-center justify-end ${
                    item.isPositive ? 'text-[#2AB7A6]' : 'text-red-400'
                  }`}
                >
                  {item.isPositive ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-1" />
                  )}
                  {item.change && item.change !== 'NaN%' ? item.change : '+0.00%'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {!isLoading && !error && filteredData.length === 0 && (
          <div className="text-center py-8">
            <p className={theme === 'light' ? 'text-gray-500' : 'theme-text-quaternary'}>No markets found</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`card p-2 md:p-4 relative ${theme === 'light' ? 'bg-white border border-0  border-gray-100' : ''}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 space-y-2 md:space-y-0">
        <div className="relative market-dropdown-container w-full md:w-auto">
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent event from bubbling up to parent dropdown
              toggleDropdown('marketDropdown');
            }}
            className={`flex items-center justify-between w-full md:w-auto space-x-1 px-2 py-1 text-xs font-medium transition-colors duration-200 border rounded-lg focus:outline-none ${
              isOpen('marketDropdown')
                ? theme === 'light' 
                  ? 'text-[#2AB7A6] bg-gray-50 border-gray-300'
                  : 'text-white theme-border'
                : theme === 'light' 
                  ? 'text-gray-900 hover:text-[#2AB7A6] bg-gray-50 border-gray-300' 
                  : 'text-white hover:text-[#2AB7A6] theme-border'
            }`}
            data-dropdown
          >
            <span>{selectedMarket}</span>
            <svg
              className={`w-3 h-3 transition-transform duration-200 ${isOpen('marketDropdown') ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Market Dropdown */}
          {isOpen('marketDropdown') && (
            <div 
              className={`absolute left-0 z-10 mt-1 w-32 origin-top-left rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-3 ${
                theme === 'light' ? 'bg-white border border-gray-200' : 'theme-bg-quaternary'
              }`} 
              data-dropdown
              onClick={(e) => e.stopPropagation()} // Prevent clicks inside dropdown from closing parent
            >
              <div className="py-1">
                {/* Group 1: Top Movers, Popular */}
                {['Top Movers', 'Popular'].map(market => (
                  <button
                    key={market}
                    onClick={() => {
                      onMarketChange(market);
                      toggleDropdown('marketDropdown');
                    }}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors duration-200 ${
                      selectedMarket === market 
                        ? theme === 'light'
                          ? 'bg-gray-600 text-white rounded'
                          : 'bg-gray-500 text-white rounded'
                        : theme === 'light' 
                          ? 'text-gray-700 hover:bg-gray-200 hover:text-white rounded' 
                          : 'theme-text-secondary hover:bg-gray-700 hover:text-white rounded'
                    }`}
                  >
                    {market}
                  </button>
                ))}
                
                {/* Separator */}
                <div className={`border-t my-1 ${theme === 'light' ? 'border-gray-200' : 'theme-border-secondary'}`}></div>
                
                {/* Group 2: Commodities, Forex, Crypto */}
                {['Commodities', 'Forex', 'Crypto'].map(market => (
                  <button
                    key={market}
                    onClick={() => handleCategoryClick(market)}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors duration-200 ${
                      selectedMarket === market 
                        ? theme === 'light'
                          ? 'bg-gray-600 text-white rounded'
                          : 'bg-gray-500 text-white rounded'
                        : theme === 'light' 
                          ? 'text-gray-700 hover:bg-gray-200 hover:text-white rounded' 
                          : 'theme-text-secondary hover:bg-gray-700 hover:text-white rounded'
                    }`}
                  >
                    {market}
                  </button>
                ))}
                
                {/* Separator */}
                <div className={`border-t my-1 ${theme === 'light' ? 'border-gray-200' : 'theme-border-secondary'}`}></div>
                
                {/* Group 3: Indices, Stocks, Futures */}
                {['Indices', 'Stocks', 'Futures'].map(market => (
                  <button
                    key={market}
                    onClick={() => {
                      onMarketChange(market);
                      toggleDropdown('marketDropdown');
                    }}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors duration-200 ${
                      selectedMarket === market 
                        ? theme === 'light'
                          ? 'bg-gray-600 text-white rounded'
                          : 'bg-gray-500 text-white rounded'
                        : theme === 'light' 
                          ? 'text-gray-700 hover:bg-gray-200 hover:text-white rounded' 
                          : 'theme-text-secondary hover:bg-gray-700 hover:text-white rounded'
                    }`}
                  >
                    {market}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2 w-full md:w-auto">
          {/* Tab Buttons */}
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex-1 md:flex-none px-2 py-1 text-xs font-bold rounded-md transition-colors duration-200 border ${
              activeTab === 'favorites'
                ? 'text-[#2AB7A6] border-[#2AB7A6]' + (theme === 'light' ? ' bg-gray-100' : ' theme-bg-quaternary')
                : theme === 'light'
                  ? 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-[#2AB7A6] hover:border-[#2AB7A6]'
                  : 'theme-bg-quaternary theme-text-secondary theme-border hover:theme-bg-quinary hover:theme-text-primary'
            }`}
          >
            Favorites
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 md:flex-none px-2 py-1 text-xs font-bold rounded-md transition-colors duration-200 border ${
              activeTab === 'all'
                ? 'text-[#2AB7A6] border-[#2AB7A6]' + (theme === 'light' ? ' bg-gray-100' : ' theme-bg-quaternary')
                : theme === 'light'
                  ? 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-[#2AB7A6] hover:border-[#2AB7A6]'
                  : 'theme-bg-quaternary theme-text-secondary theme-border hover:theme-bg-quinary hover:theme-text-primary'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
          theme === 'light' ? 'text-gray-400' : 'theme-text-quaternary'
        }`} />
        <input
          type="text"
          placeholder="Search markets..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#2AB7A6] focus:border-transparent transition-colors duration-200 ${
            theme === 'light' 
              ? 'bg-gray-50 border-gray-300 text-gray-700 placeholder-gray-400 focus:bg-white' 
              : 'theme-bg-quaternary theme-border theme-text-primary placeholder-theme-text-quaternary'
          }`}
        />
      </div>
      {/* Market List */}
      <div className="space-y-2 overflow-y-auto scrollbar-hide group market-list-scroll" 
           style={{ 
             scrollbarWidth: 'none', 
             msOverflowStyle: 'none',
            //  maxHeight: '194px' // Fixed height for both themes to maintain consistency between tabs
            // maxHeight: 'calc(100vh - 606px)'
            maxHeight: theme === 'light' ? '194px' : '190px'
           }} 
           onScroll={handleScroll}>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8 min-h-[210px]">
            <Loader 
              scale='0.5x'
              size="sm" 
              title="" 
              subtitle="" 
            />
          </div>
        )}
        
        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="text-red-400 text-center">
              <p className="text-xs font-medium">Failed to load market data</p>
              <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'}`}>{error}</p>
            </div>
            <button
              onClick={refreshData}
              className="px-4 py-2 bg-[#2AB7A6] text-black text-xs font-medium rounded-lg hover:bg-[#2AB7A6]/90 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
        
        {/* Market Data */}
        {!isLoading && !error && selectedMarket === 'Top Movers' ? (
          // Special layout for Top Movers
          <div className="space-y-4">
            {/* Top Gainers Section */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="h-4 w-4 text-[#2AB7A6]" />
                <h4 className="text-xs font-semibold text-[#2AB7A6]">Top Gainers</h4>
              </div>
              <div className="space-y-2">
                {filteredData
                  .filter(item => item.isPositive)
                  .sort((a, b) => {
                    const aChange = parseFloat(a.change.replace(/[+%]/g, ''));
                    const bChange = parseFloat(b.change.replace(/[+%]/g, ''));
                    return bChange - aChange;
                  })
                  .slice(0, 8)
                  .map(item => (
                    <div
                      key={item.symbol}
                      onClick={() => {
                        setSelectedSymbol(item.symbol);
                        onSymbolSelect?.(item.symbol);
                      }}
                      className={`flex items-center justify-between p-2 rounded-lg transition-colors duration-200 cursor-pointer ${
                        theme === 'light' 
                          ? selectedSymbol === item.symbol
                            ? 'bg-gray-100 border border-gray-200'
                            : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                          : selectedSymbol === item.symbol
                            ? 'theme-bg-quaternary'
                            : 'theme-bg-quaternary hover:theme-bg-quinary'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            toggleFavorite(item.symbol);
                          }}
                          className={`text-xs transition-all duration-200 hover:scale-110 ${
                            favorites.has(item.symbol)
                              ? 'text-yellow-400'
                              : theme === 'light' 
                                ? 'text-gray-400 hover:text-yellow-400' 
                                : 'theme-text-quaternary hover:text-yellow-400'
                          }`}
                        >
                          <Star
                            className={`h-3 w-3 ${favorites.has(item.symbol) ? 'fill-current' : ''}`}
                          />
                        </button>
                        <div>
                          <div className={`text-[11px] font-medium ${
                            theme === 'light' ? 'text-gray-900' : 'theme-text-primary'
                          }`}>
                            {item.symbol}
                          </div>
                          <div className={`text-[11px] ${
                            theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'
                          }`}>{item.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[11px] font-medium ${
                          theme === 'light' ? 'text-gray-900' : 'theme-text-primary'
                        }`}>
                          <span dangerouslySetInnerHTML={{ __html: formatPrice(parseFloat(item.price), item.symbol) }} />
                        </div>
                        <div className="text-xs font-medium text-[#2AB7A6] flex items-center">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {item.change && item.change !== 'NaN%' ? item.change : '+0.00%'}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Top Losers Section */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <TrendingDown className="h-4 w-4 text-red-400" />
                <h4 className="text-xs font-semibold text-red-400">Top Losers</h4>
              </div>
              <div className="space-y-2">
                {filteredData
                  .filter(item => !item.isPositive)
                  .sort((a, b) => {
                    const aChange = parseFloat(a.change.replace(/[-%]/g, ''));
                    const bChange = parseFloat(b.change.replace(/[-%]/g, ''));
                    return bChange - aChange;
                  })
                  .slice(0, 8)
                  .map(item => (
                    <div
                      key={item.symbol}
                      onClick={() => {
                        setSelectedSymbol(item.symbol);
                        onSymbolSelect?.(item.symbol);
                      }}
                      className={`flex items-center justify-between p-2 rounded-lg transition-colors duration-200 cursor-pointer ${
                        theme === 'light' 
                          ? selectedSymbol === item.symbol
                            ? 'bg-gray-100 border border-gray-200'
                            : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                          : selectedSymbol === item.symbol
                            ? 'theme-bg-quaternary'
                            : 'theme-bg-quaternary hover:theme-bg-quinary'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            toggleFavorite(item.symbol);
                          }}
                          className={`text-xs transition-all duration-200 hover:scale-110 ${
                            favorites.has(item.symbol)
                              ? 'text-yellow-400'
                              : theme === 'light' 
                                ? 'text-gray-400 hover:text-yellow-400' 
                                : 'theme-text-quaternary hover:text-yellow-400'
                          }`}
                        >
                          <Star
                            className={`h-3 w-3 ${favorites.has(item.symbol) ? 'fill-current' : ''}`}
                          />
                        </button>
                        <div>
                          <div className={`text-[11px] font-medium ${
                            theme === 'light' ? 'text-gray-900' : 'theme-text-primary'
                          }`}>
                            {item.symbol}
                          </div>
                          <div className={`text-[11px] ${
                            theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary'
                          }`}>{item.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[11px] font-medium ${
                          theme === 'light' ? 'text-gray-900' : 'theme-text-primary'
                        }`}>
                          <span dangerouslySetInnerHTML={{ __html: formatPrice(parseFloat(item.price), item.symbol) }} />
                        </div>
                        <div className="text-xs font-medium text-red-400 flex items-center">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          {item.change && item.change !== 'NaN%' ? item.change : '+0.00%'}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          !isLoading && !error && filteredData.map(item => (
            <div
              key={item.symbol}
              onClick={() => {
                setSelectedSymbol(item.symbol);
                onSymbolSelect?.(item.symbol);
              }}
              className={`flex items-center justify-between p-3 rounded-lg transition-colors duration-200 cursor-pointer ${
                theme === 'light' 
                  ? selectedSymbol === item.symbol
                    ? 'bg-gray-100 border border-gray-200'
                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                  : selectedSymbol === item.symbol
                    ? 'theme-bg-quaternary'
                    : 'theme-bg-quaternary hover:theme-bg-quinary'
              }`}
            >
              <div className="flex items-center space-x-3">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    toggleFavorite(item.symbol);
                  }}
                  className={`text-xs transition-all duration-200 hover:scale-110 ${
                    favorites.has(item.symbol)
                      ? 'text-yellow-400'
                      : theme === 'light' 
                        ? 'text-gray-400 hover:text-yellow-400' 
                        : 'theme-text-quaternary hover:text-yellow-400'
                  }`}
                >
                  <Star
                    className={`h-4 w-4 ${favorites.has(item.symbol) ? 'fill-current' : ''}`}
                  />
                </button>
                <div>
                  <div className={`text-[11px] font-medium ${
                    selectedSymbol === item.symbol 
                      ? (theme === 'light' ? 'text-gray-900' : 'theme-text-primary')
                      : 'theme-text-secondary'
                  }`}>
                    {item.symbol}
                  </div>
                  <div className={`text-[11px] ${
                    selectedSymbol === item.symbol 
                      ? (theme === 'light' ? 'text-gray-600' : 'theme-text-quaternary')
                      : 'theme-text-quaternary'
                  }`}>{item.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-[11px] font-medium ${
                  selectedSymbol === item.symbol 
                    ? (theme === 'light' ? 'text-gray-900' : 'theme-text-primary')
                    : 'theme-text-secondary'
                }`}>
                  <span dangerouslySetInnerHTML={{ __html: formatPrice(parseFloat(item.price), item.symbol) }} />
                </div>
                <div
                  className={`text-xs font-medium flex items-center justify-end ${
                    item.isPositive ? 'text-[#2AB7A6]' : 'text-red-400'
                  }`}
                >
                  {item.isPositive ? (
                    <TrendingUp className="h-3 w-3 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-1" />
                  )}
                  {item.change && item.change !== 'NaN%' ? item.change : '+0.00%'}
                </div>
              </div>
            </div>
          ))
        )}
        
        {/* Scroll Indicator Arrow */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <button
            onClick={() => {
              const scrollContainer = document.querySelector('.market-list-scroll') as HTMLElement;
              if (scrollContainer) {
                scrollContainer.scrollBy({ top: 100, behavior: 'smooth' });
              }
            }}
            className={`p-1 rounded-full transition-colors duration-200 cursor-pointer ${
              theme === 'light' 
                ? 'bg-gray-100 hover:bg-gray-200 border border-gray-300' 
                : 'theme-bg-quaternary hover:theme-bg-quinary'
            }`}
          >
            <ChevronDown className={`w-4 h-4 ${
              theme === 'light' ? 'text-gray-600 hover:text-gray-800' : 'theme-text-secondary hover:text-white'
            }`} />
          </button>
        </div>
      </div>

      {!isLoading && !error && filteredData.length === 0 && (
        <div className="text-center py-8">
          <p className={theme === 'light' ? 'text-gray-500' : 'theme-text-quaternary'}>No markets found</p>
        </div>
      )}

    </div>
  );
};

export default MarketTicker;
